'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import type { GetCurationServiceResponse } from '@musinsa/services-curator';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { curatorQueries } from '../query-factory';
import { EditCurationForm } from './EditCurationForm';
import { SelectGoodsSelectionStep } from './SelectGoodsSelectionStep';
import { useShopEditContext } from './ShopEditContext';
import {
  type CurationFormGoodsItem,
  type EditCurationFormData,
  editCurationFormSchema,
} from './type';
import { getCurationFormDraftKey, useCurationFormDraft } from './useCurationFormDraft';

export interface ShopCurationEditFormData extends EditCurationFormData {
  selectedProducts: CurationFormGoodsItem[];
}

interface Props {
  className?: string;
  curation?: GetCurationServiceResponse;
}

export function ShopGoodsCurationEdit({ className, curation }: Props) {
  const { step, setStep } = useShopEditContext();
  const { service } = useDeviceInfo();
  const { data: userInfo } = useQuery(curatorQueries.profile.userInfo);
  const curatorId = userInfo?.snsTitle ?? '';

  const goToSelectGoods = () => {
    if (service === SERVICE.MUSINSA && curatorId) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'goods_add_try',
        component_name: 'button',
        event_name: 'click_button',
        from: getScreenName(),
        id: curatorId,
        section_name: 'curator_curation_create',
        type: 'goods',
      });
    }
    setStep('step2');
  };

  const initialValues = useMemo<ShopCurationEditFormData>(
    () => ({
      title: curation?.title ?? '',
      description: curation?.description ?? '',
      selectedProducts: (curation?.goodsItems ?? []).map<CurationFormGoodsItem>((item) => ({
        ...item,
        // SearchedProduct는 heartCount를 사용 — 수정 시드인 likeCount를 매핑
        heartCount: item.likeCount,
      })),
    }),
    [curation],
  );

  const form = useForm<ShopCurationEditFormData>({
    resolver: zodResolver(editCurationFormSchema),
    defaultValues: initialValues,
  });
  useCurationFormDraft(
    curation?.curationId ? getCurationFormDraftKey('goods', curation.curationId) : null,
    form,
    initialValues,
  );

  return (
    <FormProvider {...form}>
      <div
        className={twMerge(
          'w-full flex flex-col grow',
          className,
          step === 'step1' ? 'bg-neutral-color-white' : '',
        )}
      >
        <EditCurationForm
          className={step !== 'step1' ? 'hidden' : ''}
          curation={curation}
          onClickSelectGoods={goToSelectGoods}
        />
        {step === 'step2' && (
          <SelectGoodsSelectionStep
            defaultCheckedProducts={form.getValues('selectedProducts')}
            onSubmit={() => setStep('step1')}
          />
        )}
      </div>
    </FormProvider>
  );
}
